import { useState, useMemo, useEffect } from "react";
import { 
    BarChart, 
    Bar, 
    XAxis, 
    YAxis, 
    CartesianGrid, 
    Tooltip, 
    Legend, 
    ResponsiveContainer, 
    PieChart, 
    Pie, 
    Cell,
    LineChart,
    Line,
    ComposedChart
} from 'recharts';
import useAuth from "./hooks/useAuth";

// Import from our new files
import { 
    median,
    linearRegression,
    weightedLinearRegression,
    calculateTrends,
    calculateDailyAverages
} from "./trendAlgorithms";
import { 
    DailyAveragesChart,
    TrendChart,
    safeFormatter
} from "./chartComponents";

const IntonationResultsDisplay = ({ results, loading, language, checkuser, forceMode }) => {
    const [viewMode, setViewMode] = useState('summary');
    const [statisticsData, setStatisticsData] = useState(null);
    const [statsLoading, setStatsLoading] = useState(false);
    const [trendView, setTrendView] = useState('dtw');
    const [trendAlgorithm, setTrendAlgorithm] = useState('global');
    const { auth, setAuth } = useAuth();

    // Helper functions object
    const helpers = useMemo(() => ({
        median,
        linearRegression,
        weightedLinearRegression
    }), []);

    // Optimized: 2000 points max for trends (high detail while maintaining performance)
    const processedTimeSeriesData = useMemo(() => {
        if (!statisticsData?.timeSeriesData) return null;
        
        const rawData = statisticsData.timeSeriesData;
        
        // Limit to 2000 points maximum for optimal performance with high detail
        let dataForTrends = rawData;
        if (rawData.length > 2000) {
            const samplingRate = Math.ceil(rawData.length / 2000); // 2000 points max
            dataForTrends = rawData.filter((_, index) => index % samplingRate === 0);
        }
        
        // Get raw data without trend calculations
        const cleanData = dataForTrends.map(point => {
            const { 
                DTWTrend, 
                intensity_DTWTrend, 
                correlationTrend, 
                intensity_correlationTrend,
                ...cleanPoint 
            } = point;
            return cleanPoint;
        });

        const trends = calculateTrends(cleanData, [
            'DTW', 'intensity_DTW', 'correlation', 'intensity_correlation'
        ], trendAlgorithm, helpers);

        // Map trends back to original data points
        if (rawData.length > dataForTrends.length) {
            // For sampled data, interpolate trends back to all points
            return rawData.map((point, index) => {
                const trendIndex = Math.min(Math.floor(index * (dataForTrends.length / rawData.length)), trends.length - 1);
                const trendPoint = trends[trendIndex];
                return {
                    ...point,
                    DTWTrend: trendPoint?.DTWTrend || point.DTW,
                    intensity_DTWTrend: trendPoint?.intensity_DTWTrend || point.intensity_DTW,
                    correlationTrend: trendPoint?.correlationTrend || point.correlation,
                    intensity_correlationTrend: trendPoint?.intensity_correlationTrend || point.intensity_correlation
                };
            });
        }

        return trends;
    }, [statisticsData, trendAlgorithm, helpers]);

    // Optimized: Use daily averages up to 4000 points, then switch to weekly
    const dailyAveragesData = useMemo(() => {
        if (!statisticsData?.timeSeriesData) return null;
        
        const timeSeriesData = statisticsData.timeSeriesData;
        
        // Use daily averages up to 4000 days, then switch to weekly
        if (timeSeriesData.length > 4000) {
            return calculateWeeklyAverages(timeSeriesData);
        }
        
        return calculateDailyAverages(timeSeriesData);
    }, [statisticsData]);

    // Fetch statistics data when switching to statistics view
    useEffect(() => {
        if (viewMode === 'statistics' && language) {
            fetchStatisticsData();
        }
    }, [viewMode, language, checkuser]);

    const fetchStatisticsData = async () => {
        setStatsLoading(true);
        try {
            const token = localStorage.getItem("token");
            let query = `${process.env.REACT_APP_USER_API_URL}/analysis/${language}/intonation_results`;
            
            const params = new URLSearchParams();
            if (checkuser) params.append('checkuser', checkuser);
            
            query += `?${params.toString()}`;

            const response = await fetch(query, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                }
            });

            if (response.ok) {
                const data = await response.json();
                processStatisticsData(data);
            } else if (response.status === 401) {
                localStorage.removeItem("token");
                setAuth({});
            }
        } catch (error) {
            console.error("Error fetching statistics data:", error);
            setStatisticsData(null);
        } finally {
            setStatsLoading(false);
        }
    };

    // Optimized: Process statistics data with 2000 point limit
    const processStatisticsData = (data) => {
        if (!data || !data.results || data.results.length === 0) {
            setStatisticsData(null);
            return;
        }

        const successfulResults = data.results.filter(r => r.comparison_metrics);
        
        if (successfulResults.length === 0) {
            setStatisticsData(null);
            return;
        }

        // Extract all metrics from successful comparisons
        const metricsData = successfulResults.map(result => result.comparison_metrics);
        
        // Calculate averages and ranges - optimized for large datasets
        const metricStats = calculateMetricStats(metricsData);
        
        // Prepare data for charts
        const barChartData = prepareBarChartData(metricStats);
        const distributionData = prepareDistributionData(metricsData);

        // Prepare time series data for trends with 2000 point limit
        const timeSeriesData = processTimeSeriesData(data.results);

        setStatisticsData({
            metricStats,
            barChartData,
            distributionData,
            timeSeriesData,
            totalComparisons: successfulResults.length,
            dateRange: getDateRange(data.results),
            allResults: data.results
        });
    };

    // Optimized metric stats calculation
    const calculateMetricStats = (metricsData) => {
        const metricKeys = [
            'DTW', 'correlation', 'slope_diff', 'derivative_DTW',
            'intensity_DTW', 'intensity_correlation', 'intensity_slope_diff', 'intensity_derivative_DTW'
        ];

        const metricStats = {};
        
        metricKeys.forEach(key => {
            const values = metricsData.map(m => m[key]).filter(v => v !== undefined && v !== null && isFinite(v));
            if (values.length > 0) {
                // For large datasets, use faster percentile calculation
                const sortedValues = [...values].sort((a, b) => a - b);
                const medianValue = median(sortedValues);
                
                // Use faster outlier detection for large datasets
                let averageValue;
                if (values.length > 2000) {
                    // For very large datasets, use median for central tendency
                    averageValue = medianValue;
                } else {
                    // For smaller datasets, use robust mean
                    const q1 = sortedValues[Math.floor(sortedValues.length * 0.25)];
                    const q3 = sortedValues[Math.floor(sortedValues.length * 0.75)];
                    const iqr = q3 - q1;
                    
                    const filteredValues = values.filter(v => 
                        v >= q1 - 1.5 * iqr && v <= q3 + 1.5 * iqr
                    );
                    
                    averageValue = filteredValues.length > 0 ? 
                        filteredValues.reduce((a, b) => a + b, 0) / filteredValues.length : 
                        values.reduce((a, b) => a + b, 0) / values.length;
                }
                
                metricStats[key] = {
                    average: averageValue,
                    median: medianValue,
                    min: Math.min(...values),
                    max: Math.max(...values),
                    count: values.length
                };
            } else {
                metricStats[key] = {
                    average: 0,
                    median: 0,
                    min: 0,
                    max: 0,
                    count: 0
                };
            }
        });

        return metricStats;
    };

    const prepareBarChartData = (metricStats) => {
        return Object.entries(metricStats)
            .filter(([key, stats]) => stats.count > 0)
            .map(([key, stats]) => ({
                metric: getMetricDisplayName(key),
                value: stats.average || 0,
                min: stats.min || 0,
                max: stats.max || 0
            }));
    };

    const prepareDistributionData = (metricsData) => {
        return [
            { name: 'Excellent', value: metricsData.filter(m => m.correlation > 0.7).length },
            { name: 'Good', value: metricsData.filter(m => m.correlation > 0.4 && m.correlation <= 0.7).length },
            { name: 'Needs Work', value: metricsData.filter(m => m.correlation <= 0.4).length }
        ];
    };

    // Optimized time series processing with 2000 point limit
    const processTimeSeriesData = (results) => {
        const individualRecordings = results
            .filter(result => result.comparison_metrics && result.date)
            .map(result => {
                const metrics = result.comparison_metrics;
                return {
                    date: result.date,
                    timestamp: new Date(result.date).getTime(),
                    DTW: metrics.DTW || 0,
                    intensity_DTW: metrics.intensity_DTW || 0,
                    correlation: metrics.correlation || 0,
                    intensity_correlation: metrics.intensity_correlation || 0,
                    slope_diff: metrics.slope_diff || 0,
                    derivative_DTW: metrics.derivative_DTW || 0,
                    originalResult: result
                };
            })
            .filter(recording => isFinite(recording.timestamp))
            .sort((a, b) => a.timestamp - b.timestamp);

        if (individualRecordings.length === 0) {
            return [];
        }

        // Limit to 2000 points for optimal performance with high detail
        if (individualRecordings.length > 2000) {
            const samplingRate = Math.ceil(individualRecordings.length / 2000);
            return individualRecordings.filter((_, index) => index % samplingRate === 0);
        }

        return individualRecordings;
    };

    // Weekly averages for large datasets
    const calculateWeeklyAverages = (data) => {
        const weekGroups = {};
        
        data.forEach(point => {
            const date = new Date(point.date);
            const weekStart = new Date(date.setDate(date.getDate() - date.getDay()));
            const weekKey = weekStart.toISOString().split('T')[0];
            
            if (!weekGroups[weekKey]) {
                weekGroups[weekKey] = {
                    date: weekKey,
                    count: 0,
                    DTW: 0,
                    correlation: 0,
                    intensity_DTW: 0,
                    intensity_correlation: 0
                };
            }
            
            weekGroups[weekKey].count++;
            weekGroups[weekKey].DTW += point.DTW || 0;
            weekGroups[weekKey].correlation += point.correlation || 0;
            weekGroups[weekKey].intensity_DTW += point.intensity_DTW || 0;
            weekGroups[weekKey].intensity_correlation += point.intensity_correlation || 0;
        });

        return Object.values(weekGroups)
            .map(group => ({
                date: group.date,
                count: group.count,
                DTW: group.DTW / group.count,
                correlation: group.correlation / group.count,
                intensity_DTW: group.intensity_DTW / group.count,
                intensity_correlation: group.intensity_correlation / group.count
            }))
            .sort((a, b) => new Date(a.date) - new Date(b.date));
    };

    const getDateRange = (results) => {
        if (!results || results.length === 0) return null;
        
        const dates = results.map(r => new Date(r.date)).filter(d => !isNaN(d));
        if (dates.length === 0) return null;
        
        const minDate = new Date(Math.min(...dates));
        const maxDate = new Date(Math.max(...dates));
        
        return {
            start: minDate.toISOString().split('T')[0],
            end: maxDate.toISOString().split('T')[0],
            days: Math.ceil((maxDate - minDate) / (1000 * 60 * 60 * 24)) + 1
        };
    };

    // Helper function to get display names for metrics
    const getMetricDisplayName = (metricKey) => {
        const names = {
            'DTW': 'Pitch DTW',
            'correlation': 'Pitch Correlation',
            'slope_diff': 'Pitch Slope Diff',
            'derivative_DTW': 'Pitch Change DTW',
            'intensity_DTW': 'Intensity DTW',
            'intensity_correlation': 'Intensity Correlation',
            'intensity_slope_diff': 'Intensity Slope Diff',
            'intensity_derivative_DTW': 'Intensity Change DTW'
        };
        return names[metricKey] || metricKey;
    };

    // Helper function to get metric descriptions
    const getMetricDescription = (metricKey) => {
        const descriptions = {
            'DTW': 'Lower values indicate better pitch contour matching',
            'correlation': 'Higher values indicate better pitch pattern similarity',
            'slope_diff': 'Lower values indicate more similar pitch trends',
            'derivative_DTW': 'Lower values indicate similar rate of pitch change',
            'intensity_DTW': 'Lower values indicate better intensity contour matching',
            'intensity_correlation': 'Higher values indicate better intensity pattern similarity',
            'intensity_slope_diff': 'Lower values indicate more similar intensity trends',
            'intensity_derivative_DTW': 'Lower values indicate similar rate of intensity change'
        };
        return descriptions[metricKey] || '';
    };

    // Enhanced trend chart with algorithm selector
    const renderTrendChart = () => {
        if (!processedTimeSeriesData || processedTimeSeriesData.length === 0) {
            return null;
        }

        const algorithmDescriptions = {
            'global': {
                name: 'Global Linear',
                description: 'Simple straight line - shows overall direction',
                bestFor: 'Clear long-term trends',
                icon: '📈'
            },
            'ema': {
                name: 'Exponential Smoothing', 
                description: 'Weighted average - reduces noise and smooths data',
                bestFor: 'Noisy data, short-term patterns',
                icon: '🔄'
            },
            'loess': {
                name: 'LOESS Regression',
                description: 'Local regression - adapts to complex patterns',
                bestFor: 'Non-linear trends, local variations',
                icon: '🎯'
            },
            'segmented': {
                name: 'Segmented Linear',
                description: 'Piecewise linear - handles changing trends',
                bestFor: 'Data with phase changes',
                icon: '📊'
            },
            'adaptive': {
                name: 'Adaptive',
                description: 'Smart switching - uses best method automatically',
                bestFor: 'Automatic optimization',
                icon: '🤖'
            }
        };

        const currentAlgo = algorithmDescriptions[trendAlgorithm];

        return (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 mb-6">
                    <div>
                        <h4 className="font-semibold text-gray-800 text-lg">Performance Trends</h4>
                        <p className="text-sm text-gray-600">
                            {processedTimeSeriesData.length} recordings analyzed
                            {statisticsData?.timeSeriesData?.length > processedTimeSeriesData.length && 
                                ` (sampled from ${statisticsData.timeSeriesData.length} total)`
                            }
                        </p>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row gap-4">
                        {/* Enhanced Trend Algorithm Selector */}
                        <div className="flex flex-wrap bg-gray-100 rounded-lg p-1 gap-1">
                            {Object.entries(algorithmDescriptions).map(([key, algo]) => (
                                <button
                                    key={key}
                                    onClick={() => setTrendAlgorithm(key)}
                                    className={`px-3 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 min-w-[100px] ${
                                        trendAlgorithm === key 
                                            ? 'bg-white text-gray-800 shadow-sm border border-gray-300' 
                                            : 'text-gray-600 hover:text-gray-800 hover:bg-gray-200'
                                    }`}
                                    title={`${algo.description} - Best for: ${algo.bestFor}`}
                                >
                                    <span className="text-base">{algo.icon}</span>
                                    <span className="hidden sm:inline">{algo.name}</span>
                                </button>
                            ))}
                        </div>
                        
                        {/* Metric Type Selector */}
                        <div className="flex bg-gray-100 rounded-lg p-1">
                            <button
                                onClick={() => setTrendView('dtw')}
                                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                    trendView === 'dtw' 
                                        ? 'bg-white text-gray-800 shadow-sm' 
                                        : 'text-gray-600 hover:text-gray-800'
                                }`}
                            >
                                DTW Trends
                            </button>
                            <button
                                onClick={() => setTrendView('correlation')}
                                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                    trendView === 'correlation' 
                                        ? 'bg-white text-gray-800 shadow-sm' 
                                        : 'text-gray-600 hover:text-gray-800'
                                }`}
                            >
                                Correlation Trends
                            </button>
                            <button
                                onClick={() => setTrendView('both')}
                                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                    trendView === 'both' 
                                        ? 'bg-white text-gray-800 shadow-sm' 
                                        : 'text-gray-600 hover:text-gray-800'
                                }`}
                            >
                                Both
                            </button>
                        </div>
                    </div>
                </div>
                
                {/* Current Algorithm Info */}
                <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-start gap-3">
                        <div className="text-2xl">{currentAlgo.icon}</div>
                        <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                                <h5 className="font-semibold text-blue-800">Current: {currentAlgo.name}</h5>
                                <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                                    {currentAlgo.bestFor}
                                </span>
                            </div>
                            <p className="text-sm text-blue-700">{currentAlgo.description}</p>
                        </div>
                    </div>
                </div>
                
                {/* Trend Chart */}
                <TrendChart 
                    data={processedTimeSeriesData}
                    trendView={trendView}
                    trendAlgorithm={trendAlgorithm}
                />
                
                {/* Algorithm Guide */}
                <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <h6 className="font-semibold text-gray-800 mb-3">Trend Algorithm Guide</h6>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                        {Object.entries(algorithmDescriptions).map(([key, algo]) => (
                            <div key={key} className={`p-3 rounded-lg border ${
                                trendAlgorithm === key ? 'bg-white border-blue-300 shadow-sm' : 'bg-gray-50 border-gray-200'
                            }`}>
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="text-lg">{algo.icon}</span>
                                    <span className="font-medium text-gray-800">{algo.name}</span>
                                </div>
                                <p className="text-gray-600 text-xs mb-2">{algo.description}</p>
                                <p className="text-gray-500 text-xs">
                                    <strong>Best for:</strong> {algo.bestFor}
                                </p>
                            </div>
                        ))}
                    </div>
                    
                    {/* Interpretation Guide */}
                    <div className="mt-4 pt-4 border-t border-gray-200">
                        <h6 className="font-semibold text-gray-800 mb-2">Interpretation Guide</h6>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                            <div>
                                <p className="font-medium mb-1">DTW Trends (Lower = Better):</p>
                                <ul className="space-y-1 text-gray-600">
                                    <li>• Steep downward slope = Rapid improvement</li>
                                    <li>• Flat line = Consistent performance</li>
                                    <li>• Upward slope = Performance decline</li>
                                </ul>
                            </div>
                            <div>
                                <p className="font-medium mb-1">Correlation Trends (Higher = Better):</p>
                                <ul className="space-y-1 text-gray-600">
                                    <li>• Steep upward slope = Rapid improvement</li>
                                    <li>• Flat line = Consistent performance</li>
                                    <li>• Downward slope = Performance decline</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    
                    {processedTimeSeriesData.length < 10 && (
                        <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                            <p className="text-yellow-700 text-xs">
                                <strong>Note:</strong> More data points needed for reliable trend analysis. 
                                Recommended: 10+ recordings for good results, 50+ for optimal analysis.
                            </p>
                        </div>
                    )}

                    {statisticsData?.timeSeriesData?.length > 2000 && (
                        <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                            <p className="text-blue-700 text-xs">
                                <strong>Performance Note:</strong> Large dataset detected ({statisticsData.timeSeriesData.length} records). 
                                Using 2000-point sampling for high-detail visualization while maintaining performance.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
                <div className="flex items-center justify-center gap-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    <div className="text-md font-medium text-gray-700">Updating results...</div>
                </div>
            </div>
        );
    }

    const successfulComparisons = results.results.filter(r => r.stored && r.comparison_metrics).length;
    const failedComparisons = results.results.filter(r => !r.stored).length;

    // Color for pie chart
    const COLORS = ['#10B981', '#F59E0B', '#EF4444'];

    return (
        <div className="space-y-6">
            {/* View Mode Toggle */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-gray-800">Processing Results</h3>
                    <div className="flex bg-gray-100 rounded-lg p-1">
                        <button
                            onClick={() => setViewMode('summary')}
                            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                                viewMode === 'summary' 
                                    ? 'bg-white text-gray-800 shadow-sm' 
                                    : 'text-gray-600 hover:text-gray-800'
                            }`}
                        >
                            Summary
                        </button>
                        <button
                            onClick={() => setViewMode('statistics')}
                            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                                viewMode === 'statistics' 
                                    ? 'bg-white text-gray-800 shadow-sm' 
                                    : 'text-gray-600 hover:text-gray-800'
                            }`}
                        >
                            Statistics
                        </button>
                        <button
                            onClick={() => setViewMode('detailed')}
                            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                                viewMode === 'detailed' 
                                    ? 'bg-white text-gray-800 shadow-sm' 
                                    : 'text-gray-600 hover:text-gray-800'
                            }`}
                        >
                            Detailed View
                        </button>
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <div className="text-2xl font-bold text-blue-700">{results.processed_files}</div>
                    <div className="text-sm text-blue-600 font-medium">Total Files Processed</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <div className="text-2xl font-bold text-green-700">{successfulComparisons}</div>
                    <div className="text-sm text-green-600 font-medium">Successful Comparisons</div>
                </div>
                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                    <div className="text-2xl font-bold text-red-700">{failedComparisons}</div>
                    <div className="text-sm text-red-600 font-medium">Failed Comparisons</div>
                </div>
            </div>

            {/* Content based on view mode */}
            {viewMode === 'summary' && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h4 className="font-semibold text-gray-800 mb-4">Processing Summary</h4>
                    <div className="space-y-3">
                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                            <span className="text-gray-600">Total files found in database:</span>
                            <span className="font-medium">{results.results.length}</span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                            <span className="text-gray-600">Files with valid pronunciation data:</span>
                            <span className="font-medium text-green-600">{successfulComparisons}</span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                            <span className="text-gray-600">Files skipped or failed:</span>
                            <span className="font-medium text-red-600">{failedComparisons}</span>
                        </div>
                        <div className="flex justify-between items-center py-2">
                            <span className="text-gray-600">Success rate:</span>
                            <span className="font-medium text-blue-600">
                                {results.results.length > 0 
                                    ? `${((successfulComparisons / results.results.length) * 100).toFixed(1)}%`
                                    : '0%'
                                }
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {viewMode === 'statistics' && statsLoading && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
                    <div className="flex items-center justify-center gap-3">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <div className="text-md font-medium text-gray-700">Loading statistics data...</div>
                    </div>
                </div>
            )}

            {viewMode === 'statistics' && statisticsData && (
                <div className="space-y-6">
                    {/* Overview Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                            <div className="text-2xl font-bold text-blue-700">{statisticsData.totalComparisons}</div>
                            <div className="text-sm text-blue-600 font-medium">Total Comparisons</div>
                        </div>
                        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                            <div className="text-2xl font-bold text-green-700">
                                {statisticsData.timeSeriesData?.length || 0}
                            </div>
                            <div className="text-sm text-green-600 font-medium">Recordings Analyzed</div>
                        </div>
                        <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                            <div className="text-2xl font-bold text-purple-700">
                                {(statisticsData.metricStats.correlation?.average || 0).toFixed(3)}
                            </div>
                            <div className="text-sm text-purple-600 font-medium">Avg Pitch Correlation</div>
                        </div>
                        <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                            <div className="text-2xl font-bold text-orange-700">
                                {(statisticsData.metricStats.intensity_correlation?.average || 0).toFixed(3)}
                            </div>
                            <div className="text-sm text-orange-600 font-medium">Avg Intensity Correlation</div>
                        </div>
                    </div>

                    {/* Performance Distribution */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <h4 className="font-semibold text-gray-800 mb-4">Performance Distribution</h4>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="h-64">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={statisticsData.distributionData}
                                            cx="50%"
                                            cy="50%"
                                            labelLine={false}
                                            label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                                            outerRadius={80}
                                            fill="#8884d8"
                                            dataKey="value"
                                        >
                                            {statisticsData.distributionData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip formatter={(value) => [`${value} comparisons`, 'Count']} />
                                        <Legend />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="space-y-3">
                                <div className="flex items-center gap-2">
                                    <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                                    <span className="text-sm font-medium">Excellent (Correlation &gt; 0.7)</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-4 h-4 bg-yellow-500 rounded-full"></div>
                                    <span className="text-sm font-medium">Good (Correlation 0.4-0.7)</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-4 h-4 bg-red-500 rounded-full"></div>
                                    <span className="text-sm font-medium">Needs Work (Correlation ≤ 0.4)</span>
                                </div>
                                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                                    <p className="text-sm text-gray-600">
                                        Based on {statisticsData.totalComparisons} successful comparisons
                                        {statisticsData.dateRange && (
                                            <span> from {statisticsData.dateRange.start} to {statisticsData.dateRange.end}</span>
                                        )}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Daily Averages Chart */}
                    {dailyAveragesData && <DailyAveragesChart data={dailyAveragesData} />}

                    {/* Trend Chart with Enhanced Algorithm Selection */}
                    {renderTrendChart()}

                    {/* Average Metrics */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <h4 className="font-semibold text-gray-800 mb-4">Average Performance Metrics</h4>
                        <div className="h-80">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={statisticsData.barChartData}
                                    margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis 
                                        dataKey="metric" 
                                        angle={-45} 
                                        textAnchor="end"
                                        height={80}
                                        tick={{ fontSize: 12 }}
                                    />
                                    <YAxis tick={{ fontSize: 12 }} />
                                    <Tooltip 
                                        formatter={(value, name) => [value?.toFixed(3), 'Average']}
                                        labelFormatter={(label) => label}
                                    />
                                    <Bar dataKey="value" fill="#8884d8" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Detailed Metric Statistics */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {Object.entries(statisticsData.metricStats)
                            .filter(([key, stats]) => stats.count > 0) // Only show metrics with data
                            .map(([key, stats]) => (
                            <div key={key} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                <h5 className="font-medium text-gray-800 mb-2">{getMetricDisplayName(key)}</h5>
                                <p className="text-sm text-gray-600 mb-3">{getMetricDescription(key)}</p>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Average:</span>
                                        <span className="font-medium">{(stats.average || 0).toFixed(3)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Median:</span>
                                        <span className="font-medium">{(stats.median || 0).toFixed(3)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Range:</span>
                                        <span className="font-medium">{(stats.min || 0).toFixed(3)} - {(stats.max || 0).toFixed(3)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Samples:</span>
                                        <span className="font-medium">{stats.count || 0}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {viewMode === 'statistics' && !statsLoading && !statisticsData && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
                    <div className="text-gray-400 text-4xl mb-2">📊</div>
                    <h4 className="font-semibold text-gray-600 mb-2">No Historical Data Available</h4>
                    <p className="text-gray-500">No intonation analysis results found in the database for {language}</p>
                    <button
                        onClick={fetchStatisticsData}
                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Retry Load Data
                    </button>
                </div>
            )}

            {viewMode === 'detailed' && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h4 className="font-semibold text-gray-800 mb-4">Detailed Processing Log</h4>
                    <div className="max-h-96 overflow-y-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 sticky top-0">
                                <tr>
                                    <th className="px-4 py-2 text-left font-medium text-gray-700">File</th>
                                    <th className="px-4 py-2 text-left font-medium text-gray-700">Date</th>
                                    <th className="px-4 py-2 text-left font-medium text-gray-700">Status</th>
                                    <th className="px-4 py-2 text-left font-medium text-gray-700">Details</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {results.results.map((result, index) => (
                                    <tr key={index} className="hover:bg-gray-50">
                                        <td className="px-4 py-2 text-gray-600 font-mono text-xs">
                                            {result.file ? result.file.split('/').pop() : 'Unknown file'}
                                        </td>
                                        <td className="px-4 py-2 text-gray-600">{result.date || 'N/A'}</td>
                                        <td className="px-4 py-2">
                                            {result.stored ? (
                                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                    Stored
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs">
                                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                    Failed
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-2 text-gray-500 text-xs">
                                            {result.error ? (
                                                <span className="text-red-600">{result.error}</span>
                                            ) : (
                                                <span className="text-green-600">Successfully compared with narrator</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Additional Information */}
            <div className="bg-yellow-50 rounded-xl border border-yellow-200 p-4">
                <div className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                        <h4 className="font-medium text-yellow-800">About Intonation Analysis</h4>
                        <p className="text-yellow-700 text-sm mt-1">
                            This analysis compares user audio files with narrator reference files using Dynamic Time Warping (DTW) 
                            to measure pitch contour similarity. Results are stored in the database for further analysis.
                            {forceMode && " Force mode was used to re-analyze all files."}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default IntonationResultsDisplay;